'use client';

import { useCallback } from 'react';
import { FilterList, FilterListProps } from '@/components/Filters/FilterList';
import { ActiveListFilter, ListFacetsAndCategories } from '@/components/Filters/listFilter';
import { GetPathNameArgs } from '@/i18n/navigation';
import { getCategoriesById, getDepartmentById } from '@/utility/listPages';
import { Locale } from '@/utility/locale';
import { getShowroomHrefArgs } from '@/utility/urls';

type ShowroomFilterList = Omit<FilterListProps, 'getPathnameArgs'> & {
  locale: Locale;
  userSlug: string;
};

export const ShowroomFilterList = ({
  locale,
  filter,
  activeFilter,
  userSlug,
}: ShowroomFilterList) => {
  const getPathnameArgs = useCallback(
    (
      newFilter: Partial<ListFacetsAndCategories>,
      newActiveFilter: ActiveListFilter
    ): GetPathNameArgs => {
      const {
        department: newDepartment,
        category: newCategory,
        searchTerm,
        ...searchParams
      } = newActiveFilter;

      const {
        appearance,
        brand,
        fit,
        materials,
        neckline,
        refinement,
        size,
        sleeve,
        style,
        sort,
        ...otherParams
      } = searchParams;

      return {
        href: getShowroomHrefArgs({
          userSlug,
          departmentSlug: newDepartment
            ? getDepartmentById(newFilter.departments ?? [], newDepartment)?.slug
            : undefined,
          categorySlugs: newCategory
            ? getCategoriesById(newFilter.categories ?? [], newCategory).map(
                (category) => category.slug
              )
            : undefined,
          searchTerm: searchTerm,
          queryParams: {
            ...otherParams,
            sort: sort as ProductListPageSort,
            appearance: appearance,
            brand: brand,
            fit: fit,
            materials: materials ? [materials] : undefined,
            neckline: neckline,
            refinement: refinement ? [refinement] : undefined,
            size: size,
            sleeve: sleeve,
            style: style ? [style] : undefined,
          },
        }),
        locale: locale,
      };
    },
    [locale, userSlug]
  );

  return (
    <FilterList
      filter={filter}
      activeFilter={activeFilter}
      getPathnameArgs={getPathnameArgs}
      sortOptions={['weight', 'new']}
    />
  );
};
